import type ElementBaseModel from '@src/slide/domain/ui-model/element-modeles/base-modeles/element-base-model';
import type TableModel from '@src/slide/domain/ui-model/element-modeles/table-model';
import { getRunText as getRunTextV3 } from '../lib/ParserUtils';
import TablePasteParser from '../lib/TablePasteParser';
import type { SdkDataInheritItem } from '../store-manager';
import { DataType } from '@tencent/slide-sdk-model';

export enum TextType {
    ELEMENT,
    TEXT,
    ELEMENT_INHERIT_AND_NO_INHERIT
}

interface ElementTextData {
    type: TextType.ELEMENT;
    data: ElementBaseModel[];
}

interface ElementInheritTextData {
    type: TextType.ELEMENT_INHERIT_AND_NO_INHERIT;
    data: SdkDataInheritItem[];
}

interface StringTextData {
    type: TextType.TEXT;
    data: string;
}
type TextData = ElementTextData | ElementInheritTextData | StringTextData;

class TextPreparePlugin {
    /**
     * 从现有textData中获取文本，可能是文本，可能是Model
     * @param {TextData} textData
     * @returns {string}
     */
    public getTextString(textData: TextData): string {
        let text = textData.data;
        if (textData.type === TextType.ELEMENT) {
            text = this.plainTextFromElements(textData.data);
        } else if (textData.type === TextType.ELEMENT_INHERIT_AND_NO_INHERIT) {
            const fakeElements = textData.data
                .map(({ inheritedSdkData }) => ({ sdkData: inheritedSdkData }));
            text = this.plainTextFromElements(fakeElements as unknown as ElementBaseModel[]);
        }
        return text as string;
    }

    /**
     * 将文本string转为blob的过程
     * @param {TextData} textData
     * @returns {Blob}
     */
    public getTextBlob(textData: TextData): Blob {
        return new Blob([this.getTextString(textData)], { type: 'text/plain' });
    }

    /**
     * 将文本blob转为string的过程
     * @param blobData blob data
     * @returns {Promise<void>}
     */
    public async textBlobToText(blobData: Blob): Promise<string> {
        return await blobData.text();
    }

    /**
     * 从Model中获取文本
     * @param {Array<ElementBaseModel>} elements
     * @returns {string}
     */
    private plainTextFromElements(elements: Array<ElementBaseModel>) {
        if (this.isTable(elements)) {
            return this.plainTextFromTable(elements);
        }
        if (this.isAddon(elements)) {
            return this.plainTextFromAddon(elements);
        }
        return this.plainTextFromShape(elements);
    }

    private plainTextFromShape(elements) {
        const contentArr: string[] = [];
        elements.forEach((element: ElementBaseModel) => {
            const p = (element.sdkData as any)?.txBody?.p;
            p?.forEach((paragraph) => {
                paragraph.egTextRun.forEach((textRun) => {
                    contentArr.push(getRunTextV3(textRun));
                });
            });
        });
        return contentArr.join('');
    }

    private plainTextFromTable(tables) {
        const contentArr: string[] = [];
        tables.forEach((table: TableModel) => {
            contentArr.push(TablePasteParser.getSingleton().parseSdkDataToPlainText(table.sdkData));
        });
        return contentArr.join('');
    }

    private plainTextFromAddon(elements) {
        return elements.map(element => element.props?.addon?.originUrl).join(' ');
    }

    private isTable(elements) {
        const tableSdkType = DataType.GraphicalObjectFrameTable;
        return elements.every(element => element.sdkData.type === tableSdkType);
    }

    private isAddon(elements) {
        return elements.every(element => element.props?.addon?.displayMode === 'iframe');
    }
}

export default TextPreparePlugin;
